import { getActiveRoleRequestHeaders } from "@/lib/utils/api/active-role-header";
import { resolveOpenApiBasePath } from "@/lib/utils/api/openapi-configuration";
import { getEffectiveTenantApiHeaders } from "@/lib/utils/env/effective-tenant";
import {
  type BalanceUnitStatement,
  BalanceUnitStatementFromJSON,
  type GetBalanceStatementByStudentRequest,
  GetBalanceStatementByStudentRequestToJSON,
  type GetBalanceStatementByUserRequest,
  GetBalanceStatementByUserRequestToJSON,
} from "@/openapi/generated/models";
import { IdNameResultOfintFromJSON } from "@/openapi/generated/models/IdNameResultOfint";
import { readStatementAccountCredit } from "./statement-account-credit";
import { readStatementTuitionBreakdown } from "./statement-tuition-breakdown";

export type BalanceStatementRequest = GetBalanceStatementByStudentRequest;

export function buildStudentStatementsUrl(
  basePath: string,
  studentId: number,
  page: number,
  pageSize: number,
): string {
  const url = new URL(
    `${basePath.replace(/\/+$/, "")}/api/balance/students/${encodeURIComponent(String(studentId))}/statements`,
  );
  url.searchParams.set("page", String(page));
  url.searchParams.set("pageSize", String(pageSize));
  return url.toString();
}

export function toStudentStatementRequestBody(
  request: GetBalanceStatementByStudentRequest,
): GetBalanceStatementByStudentRequest {
  return {
    userId: request.userId ?? null,
    studentId: request.studentId ?? null,
    from: request.from ?? null,
    to: request.to ?? null,
    displayStatus: request.displayStatus ?? null,
    descriptionSearch: request.descriptionSearch ?? null,
    transactionCode: request.transactionCode ?? null,
    period: request.period ?? null,
  };
}

export function toUserStatementRequestBody(
  request:
    | GetBalanceStatementByStudentRequest
    | GetBalanceStatementByUserRequest,
): GetBalanceStatementByUserRequest {
  return {
    userId: request.userId ?? null,
    from: request.from ?? null,
    to: request.to ?? null,
    displayStatus: request.displayStatus ?? null,
    descriptionSearch: request.descriptionSearch ?? null,
    transactionCode: request.transactionCode ?? null,
    period: request.period ?? null,
  };
}

function readStatementExternalStatus(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function parseStudentStatementResponse(
  json: unknown,
): BalanceUnitStatement {
  const statement = BalanceUnitStatementFromJSON(json);
  const accountCredit = readStatementAccountCredit(json);
  if (statement != null && accountCredit != null) {
    Object.assign(statement, { totalAccountCredit: accountCredit });
  }
  if (!statement.entries || json == null || typeof json !== "object") {
    return statement;
  }

  const rawEntries = (json as { entries?: unknown[] }).entries;
  if (!Array.isArray(rawEntries)) return statement;

  const statementStudentJson = (json as { student?: unknown }).student;
  const statementStudent =
    statementStudentJson != null
      ? IdNameResultOfintFromJSON(statementStudentJson)
      : undefined;

  statement.entries = statement.entries.map((entry, index) => {
    const raw = rawEntries[index];
    if (!raw || typeof raw !== "object") return entry;
    const studentJson = (raw as { student?: unknown }).student;
    const student =
      studentJson != null
        ? IdNameResultOfintFromJSON(studentJson)
        : (entry.student ?? statementStudent);
    const tuitionBreakdown = readStatementTuitionBreakdown(
      (raw as { tuitionBreakdown?: unknown }).tuitionBreakdown,
    );
    const externalStatus = readStatementExternalStatus(
      (raw as { externalStatus?: unknown }).externalStatus,
    );
    if (student == null && tuitionBreakdown == null && externalStatus == null) {
      return entry;
    }
    return {
      ...entry,
      ...(student != null ? { student } : {}),
      ...(tuitionBreakdown != null ? { tuitionBreakdown } : {}),
      ...(externalStatus != null ? { externalStatus } : {}),
    };
  });

  return statement;
}

export function buildUserStatementsUrl(
  basePath: string,
  userId: number,
  page: number,
  pageSize: number,
): string {
  const url = new URL(
    `${basePath.replace(/\/+$/, "")}/api/balance/users/${encodeURIComponent(String(userId))}/statements`,
  );
  url.searchParams.set("page", String(page));
  url.searchParams.set("pageSize", String(pageSize));
  return url.toString();
}

export function emptyBalanceUnitStatement(): BalanceUnitStatement {
  return parseStudentStatementResponse({
    entries: [],
    totalCount: 0,
  });
}

export function readBalanceStatementResponse(
  status: number,
  json: unknown,
  errorLabel: string,
): BalanceUnitStatement {
  if (status === 404) {
    return emptyBalanceUnitStatement();
  }

  if (status < 200 || status >= 300) {
    throw new Error(`${errorLabel} (${status})`);
  }

  return parseStudentStatementResponse(json);
}

export async function fetchUserBalanceStatements(options: {
  accessToken: string;
  tenantId: number;
  userId: number;
  request: BalanceStatementRequest;
  page: number;
  pageSize: number;
}) {
  const url = buildUserStatementsUrl(
    resolveOpenApiBasePath(),
    options.userId,
    options.page,
    options.pageSize,
  );

  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...getEffectiveTenantApiHeaders(),
      ...getActiveRoleRequestHeaders(),
      Authorization: `Bearer ${options.accessToken}`,
      "X-Tenant-ID": String(options.tenantId),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(
      GetBalanceStatementByUserRequestToJSON(
        toUserStatementRequestBody(options.request),
      ),
    ),
  });

  if (response.status === 404 || !response.ok) {
    return readBalanceStatementResponse(
      response.status,
      null,
      "Failed to load member statement",
    );
  }

  return readBalanceStatementResponse(
    response.status,
    await response.json(),
    "Failed to load member statement",
  );
}

export async function fetchStudentBalanceStatements(options: {
  accessToken: string;
  tenantId: number;
  studentId: number;
  request: BalanceStatementRequest;
  page: number;
  pageSize: number;
}) {
  const url = buildStudentStatementsUrl(
    resolveOpenApiBasePath(),
    options.studentId,
    options.page,
    options.pageSize,
  );

  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...getEffectiveTenantApiHeaders(),
      ...getActiveRoleRequestHeaders(),
      Authorization: `Bearer ${options.accessToken}`,
      "X-Tenant-ID": String(options.tenantId),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(
      GetBalanceStatementByStudentRequestToJSON(
        toStudentStatementRequestBody(options.request),
      ),
    ),
  });

  if (response.status === 404 || !response.ok) {
    return readBalanceStatementResponse(
      response.status,
      null,
      "Failed to load student statement",
    );
  }

  return readBalanceStatementResponse(
    response.status,
    await response.json(),
    "Failed to load student statement",
  );
}
